// Season detail — F6 Slice 3. Reads :seasonId from the route and loads the
// season shell PLUS its divisions (Promise.allSettled, so the header still
// renders when the divisions fetch fails). Surfaces:
//   - Header: season name <h1>, UTC date range, status Badge, and a back-link to
//     the parent league (/leagues/{league_id} derived from season.league_id).
//   - Divisions section: one row per division (name, tier range, max_players,
//     status Badge, and an HONEST registered count "N / max_players" read
//     STRAIGHT off division.registration_count — never a getRegistrations call).
//     Honest empty ("No divisions yet") + "New division" CTA; an independent
//     destructive banner when the divisions fetch rejects.
//   - Create division dialog: name (required, trim-gated), max_players (number,
//     default 16, must be >= 1), min_tier + max_tier (optional Selects from the
//     7 real tiers + an "Any" sentinel that omits the bound). If BOTH tiers are
//     set, min must not EXCEED max by LEAGUE_TIERS order — blocked client-side.
//   - Registrations (READ-ONLY): each division row reveals its registrations on
//     expand, lazily fetching getRegistrations(divisionId) ONLY on first expand
//     (avoids N+1 on page load). Each registration shows a RESOLVED player
//     display name (via getPlayerDisplayName — never the raw UID) + a status
//     Badge. Honest "No registrations" empty + a per-division error banner on
//     failure. There is NO register/withdraw/remove control — registration write
//     is player self-service.
//
// Renders ONLY real backend fields. season_id / league_id / division_id /
// registration_id / player_id are NEVER shown as visible text. Tier/status are
// mapped to human labels (never the raw enum). Analog: league-detail.tsx
// (detail page + Promise.allSettled + State-union/re-entrancy/dialog conventions).

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { notifications } from "@/app/notifications";
import {
  LEAGUE_TIERS,
  createDivision,
  createMatch,
  createSession,
  getDivisions,
  getPlayerDisplayName,
  getRegistrations,
  getSeason,
  getSessionMatches,
  getSessionRegistrations,
  getSessions,
  getStandings,
  reportMatchResult,
  type LeagueDivisionResponse,
  type LeagueMatchResponse,
  type LeagueRegistrationResponse,
  type LeagueSeasonResponse,
  type LeagueSessionRegistrationResponse,
  type LeagueSessionResponse,
  type LeagueStandingResponse,
  type LeagueTier,
} from "@/libs/leaguesApi";
import {
  DIVISION_STATUS_LABELS,
  MATCH_STATUS_LABELS,
  REGISTRATION_STATUS_LABELS,
  SEASON_STATUS_LABELS,
  SESSION_STATUS_LABELS,
  TIER_LABELS,
  tierRangeLabel,
} from "./league-display";

// UTC date formatter — "Jun 1, 2026". The backend serializes season
// start_date / end_date as UTC-midnight instants; formatting in the browser's
// local zone would drift the day back in any west-of-UTC env, so we pin
// timeZone: "UTC". LOCAL copy of the league-detail formatter (cross-module
// consolidation is a tracked follow-up — not done in this slice).
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatDateRange(
  start: string,
  end: string | null | undefined,
): string {
  const startLabel = formatDate(start);
  if (!end) return startLabel;
  return `${startLabel} – ${formatDate(end)}`;
}

// UTC time-of-day formatter — "6:00 PM". A session's start_time / end_time are
// UTC instants on the wire; formatting in the browser's local zone would shift
// the displayed clock time, so we pin timeZone: "UTC" (same discipline as
// formatDate). Used for the "start – end" range on each session row.
function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

function formatTimeRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  return `${formatTime(start)} – ${formatTime(end)}`;
}

// "Any" sentinel for the tier Selects. The UI carries this literal in state and
// the create handler maps it back to a null/omitted tier — it NEVER round-trips as
// a tier value.
const TIER_ANY = "__any__";

type LifecycleColor = "default" | "error" | "info" | "success" | "warning";

function lifecycleColor(status: string): LifecycleColor {
  if (["cancelled", "withdrawn"].includes(status)) return "error";
  if (["active", "in_progress", "open", "registered"].includes(status)) {
    return "success";
  }
  if (["registration", "scheduled"].includes(status)) return "info";
  if (["full", "waitlisted"].includes(status)) return "warning";
  return "default";
}

// The per-division lazy registration load state. `entries` is undefined until a
// first successful fetch resolves; `error` is the per-division banner (distinct
// from an empty list so a failed fetch never masquerades as "No registrations").
interface RegState {
  loading: boolean;
  entries?: LeagueRegistrationResponse[];
  error?: string;
  // Resolved player_id -> display name, populated as each name resolves.
  names: Record<string, string>;
}

// The per-division lazy standings load state. Mirrors RegState exactly:
// `entries` is undefined until a first successful fetch resolves; `error` is the
// per-division banner, distinct from an empty list so a failed fetch never
// masquerades as "No standings". `names` resolves each player_id -> display name
// so the table never renders a raw UID.
interface StandingState {
  loading: boolean;
  entries?: LeagueStandingResponse[];
  error?: string;
  names: Record<string, string>;
}

// The per-session lazy matches load state. Mirrors RegState/StandingState:
// `entries` is the session's matches (undefined until a first successful fetch
// resolves); `error` is the per-session matches banner, distinct from an empty
// list so a failed fetch never masquerades as "No matches yet". `pool` is the
// session's READ-ONLY registered player pool used to build matchups (undefined
// if its independent fetch failed — a pool failure must NOT sink the matches
// list, so the two are loaded via Promise.allSettled). `names` resolves every
// player_id seen in either matches or the pool -> a display name, so neither the
// list nor the team pickers ever render a raw UID.
interface MatchState {
  loading: boolean;
  entries?: LeagueMatchResponse[];
  error?: string;
  pool?: LeagueSessionRegistrationResponse[];
  names: Record<string, string>;
}

export function SeasonDetail() {
  const { seasonId = "" } = useParams<{ seasonId: string }>();

  const [season, setSeason] = useState<LeagueSeasonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Divisions load independently of the season (Promise.allSettled) — the header
  // must render even if divisions fail. `divisionsError` is the divisions-
  // specific banner, distinct from the empty state so a failed fetch never
  // masquerades as "no divisions".
  const [divisions, setDivisions] = useState<LeagueDivisionResponse[]>([]);
  const [divisionsError, setDivisionsError] = useState<string | null>(null);

  // Sessions load independently of the season AND divisions (a THIRD
  // Promise.allSettled branch) — a sessions failure must not sink the header or
  // the divisions section, and vice-versa. `sessionsError` is the sessions-
  // specific banner, distinct from the empty state so a failed fetch never
  // masquerades as "no sessions".
  const [sessions, setSessions] = useState<LeagueSessionResponse[]>([]);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  // Per-division expand + lazy registrations state, keyed by division_id.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [regs, setRegs] = useState<Record<string, RegState>>({});

  // Per-division standings expand + lazy standings state, keyed by division_id —
  // a sibling toggle to the registrations expand, mirroring its lazy/cache/retry
  // discipline exactly.
  const [standingsOpen, setStandingsOpen] = useState<Record<string, boolean>>(
    {},
  );
  const [standings, setStandings] = useState<Record<string, StandingState>>({});

  // Create division dialog state.
  const [createOpen, setCreateOpen] = useState(false);
  const [divName, setDivName] = useState("");
  const [divMaxPlayers, setDivMaxPlayers] = useState("16");
  const [divMinTier, setDivMinTier] = useState<string>(TIER_ANY);
  const [divMaxTier, setDivMaxTier] = useState<string>(TIER_ANY);
  const [savingDiv, setSavingDiv] = useState(false);
  const divInFlight = useRef(false);

  // Create session dialog state.
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessNumber, setSessNumber] = useState("");
  const [sessDate, setSessDate] = useState("");
  const [sessStart, setSessStart] = useState("");
  const [sessEnd, setSessEnd] = useState("");
  const [savingSess, setSavingSess] = useState(false);
  const sessInFlight = useRef(false);

  // Per-session "Matches" expand + lazy matches/pool state, keyed by session_id —
  // a sibling toggle to the divisions' registrations/standings expands, mirroring
  // their lazy/cache/retry discipline exactly.
  const [matchesOpen, setMatchesOpen] = useState<Record<string, boolean>>({});
  const [matches, setMatches] = useState<Record<string, MatchState>>({});

  // Create-match (matchup generator) dialog state. Scoped to the session whose
  // "Add match" was clicked. Team rosters are sets of player_id; stage defaults
  // to 1. court_id is OMITTED this slice (no court picker).
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchSessionId, setMatchSessionId] = useState<string | null>(null);
  const [team1Ids, setTeam1Ids] = useState<string[]>([]);
  const [team2Ids, setTeam2Ids] = useState<string[]>([]);
  const [matchStage, setMatchStage] = useState("1");
  const [matchFormError, setMatchFormError] = useState<string | null>(null);
  const [savingMatch, setSavingMatch] = useState(false);
  const matchInFlight = useRef(false);

  // Report-result dialog state. Scoped to the (scheduled) match whose "Report
  // result" was clicked, plus its owning session_id (so the success path can
  // refetch that session's matches).
  const [resultOpen, setResultOpen] = useState(false);
  const [resultMatchId, setResultMatchId] = useState<string | null>(null);
  const [resultSessionId, setResultSessionId] = useState<string | null>(null);
  const [resultScore, setResultScore] = useState("");
  const [resultWinner, setResultWinner] = useState<1 | 2 | null>(null);
  const [savingResult, setSavingResult] = useState(false);
  const resultInFlight = useRef(false);

  useEffect(() => {
    if (!seasonId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDivisionsError(null);
    setSessionsError(null);
    (async () => {
      // THREE independent branches — one failure never sinks the others.
      const [seasonRes, divisionsRes, sessionsRes] = await Promise.allSettled([
        getSeason(seasonId),
        getDivisions(seasonId),
        getSessions(seasonId),
      ]);
      if (cancelled) return;
      if (seasonRes.status === "fulfilled") {
        setSeason(seasonRes.value);
      } else {
        setError(
          seasonRes.reason instanceof Error
            ? seasonRes.reason.message
            : "Failed to load this season.",
        );
      }
      if (divisionsRes.status === "fulfilled") {
        setDivisions(divisionsRes.value);
      } else {
        setDivisionsError(
          "We couldn't load the divisions for this season. Please try again.",
        );
      }
      if (sessionsRes.status === "fulfilled") {
        setSessions(sessionsRes.value);
      } else {
        setSessionsError(
          "We couldn't load the sessions for this season. Please try again.",
        );
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [seasonId]);

  // Divisions ordered oldest-first by created_at (stable, deterministic) — never
  // trust the server order. A row with an unparseable created_at sorts last.
  const sortedDivisions = useMemo(
    () =>
      [...divisions].sort((a, b) => {
        const at = new Date(a.created_at).getTime();
        const bt = new Date(b.created_at).getTime();
        const av = Number.isNaN(at) ? Infinity : at;
        const bv = Number.isNaN(bt) ? Infinity : bt;
        return av - bv;
      }),
    [divisions],
  );

  // Sessions ordered by session_number ASC (stable, deterministic) — never trust
  // the server order. A row with a non-finite session_number sorts last.
  const sortedSessions = useMemo(
    () =>
      [...sessions].sort((a, b) => {
        const an = Number.isFinite(a.session_number)
          ? a.session_number
          : Infinity;
        const bn = Number.isFinite(b.session_number)
          ? b.session_number
          : Infinity;
        return an - bn;
      }),
    [sessions],
  );

  // Lazy-load a division's registrations on FIRST expand only (avoids an N+1
  // eager fetch on page mount). A collapse keeps successfully-loaded data so a
  // re-expand is instant and does not re-fetch. A never-loaded division — or one
  // whose previous fetch ERRORED — fetches on (re-)expand, so a transient failure
  // is recoverable without a page reload.
  const toggleExpand = (division: LeagueDivisionResponse) => {
    const id = division.division_id;
    const isOpen = !!expanded[id];
    setExpanded((prev) => ({ ...prev, [id]: !isOpen }));
    if (isOpen) return;
    // Already loaded (entries) or in flight? Don't refetch on re-expand — that
    // would dupe an in-flight request or pointlessly refetch a settled list. A
    // prior ERROR state is deliberately NOT short-circuited: a transient
    // 5xx/network failure must be RETRYABLE via collapse + re-expand. The fetch
    // path below resets to {loading:true, ...} first, so the stale error banner
    // clears on retry.
    const existing = regs[id];
    if (existing && (existing.loading || existing.entries)) return;
    setRegs((prev) => ({ ...prev, [id]: { loading: true, names: {} } }));
    (async () => {
      try {
        const entries = await getRegistrations(id);
        const names: Record<string, string> = {};
        await Promise.all(
          entries.map(async (r) => {
            names[r.player_id] = await getPlayerDisplayName(r.player_id);
          }),
        );
        setRegs((prev) => ({
          ...prev,
          [id]: { loading: false, entries, names },
        }));
      } catch {
        setRegs((prev) => ({
          ...prev,
          [id]: {
            loading: false,
            names: {},
            error:
              "We couldn't load the registrations for this division. Please try again.",
          },
        }));
      }
    })();
  };

  // Lazy-load a division's standings on FIRST expand only — mirrors the
  // registrations expand EXACTLY: a collapse keeps successfully-loaded data so a
  // re-expand is instant and does not re-fetch; a never-loaded division — or one
  // whose previous fetch ERRORED — fetches on (re-)expand, so a transient failure
  // is recoverable without a page reload. The cache guard short-circuits on
  // {loading || entries} but deliberately NOT on {error}, so the error banner is
  // retryable.
  const toggleStandings = (division: LeagueDivisionResponse) => {
    const id = division.division_id;
    const isOpen = !!standingsOpen[id];
    setStandingsOpen((prev) => ({ ...prev, [id]: !isOpen }));
    if (isOpen) return;
    const existing = standings[id];
    if (existing && (existing.loading || existing.entries)) return;
    setStandings((prev) => ({ ...prev, [id]: { loading: true, names: {} } }));
    (async () => {
      try {
        const entries = await getStandings(id);
        const names: Record<string, string> = {};
        await Promise.all(
          entries.map(async (s) => {
            names[s.player_id] = await getPlayerDisplayName(s.player_id);
          }),
        );
        setStandings((prev) => ({
          ...prev,
          [id]: { loading: false, entries, names },
        }));
      } catch {
        setStandings((prev) => ({
          ...prev,
          [id]: {
            loading: false,
            names: {},
            error:
              "We couldn't load the standings for this division. Please try again.",
          },
        }));
      }
    })();
  };

  // Resolve the display names for a batch of player_ids (de-duped), returning a
  // player_id -> name map. A single seam used by both the matches load (teams)
  // and the pool load (pickers) so neither ever renders a raw UID.
  const resolveNames = async (
    playerIds: string[],
  ): Promise<Record<string, string>> => {
    const unique = Array.from(new Set(playerIds));
    const names: Record<string, string> = {};
    await Promise.all(
      unique.map(async (pid) => {
        names[pid] = await getPlayerDisplayName(pid);
      }),
    );
    return names;
  };

  // Lazy-load a session's matches AND its registered player pool on FIRST expand
  // only (avoids an N+1 eager fetch on page mount). Mirrors the divisions'
  // registrations/standings expands EXACTLY: a collapse keeps successfully-loaded
  // data so a re-expand is instant and does not re-fetch; a never-loaded session —
  // or one whose previous MATCHES fetch ERRORED — fetches on (re-)expand, so a
  // transient failure is recoverable without a page reload. The cache guard
  // short-circuits on {loading || entries} but deliberately NOT on {error}, so the
  // matches error banner is retryable.
  //
  // Matches + pool load via Promise.allSettled so the two are INDEPENDENT: a pool
  // (registrations) fetch failure must not sink the matches list, and a matches
  // failure surfaces a retryable banner regardless of the pool's fate.
  const toggleMatches = (session: LeagueSessionResponse) => {
    const id = session.session_id;
    const isOpen = !!matchesOpen[id];
    setMatchesOpen((prev) => ({ ...prev, [id]: !isOpen }));
    if (isOpen) return;
    const existing = matches[id];
    if (existing && (existing.loading || existing.entries)) return;
    setMatches((prev) => ({ ...prev, [id]: { loading: true, names: {} } }));
    (async () => {
      const [matchesRes, poolRes] = await Promise.allSettled([
        getSessionMatches(id),
        getSessionRegistrations(id),
      ]);
      // A matches failure is the per-session banner (retryable). The pool result
      // is independent — a pool failure leaves `pool` undefined but never blocks
      // the matches list from rendering.
      if (matchesRes.status === "rejected") {
        setMatches((prev) => ({
          ...prev,
          [id]: {
            loading: false,
            names: {},
            pool: poolRes.status === "fulfilled" ? poolRes.value : undefined,
            error:
              "We couldn't load the matches for this session. Please try again.",
          },
        }));
        return;
      }
      const entries = matchesRes.value;
      const pool = poolRes.status === "fulfilled" ? poolRes.value : undefined;
      // Resolve every player_id referenced by a match team OR the pool so neither
      // surface renders a raw UID.
      const ids = [
        ...entries.flatMap((m) => [
          ...m.team1_player_ids,
          ...m.team2_player_ids,
        ]),
        ...(pool ?? []).map((p) => p.player_id),
      ];
      const names = await resolveNames(ids);
      setMatches((prev) => ({
        ...prev,
        [id]: { loading: false, entries, pool, names },
      }));
    })();
  };

  // Refetch ONE session's matches after a successful create/report — keeps the
  // existing pool + names, merges any newly-seen player names. Used by the
  // create-match and report-result success paths so the list reflects the server
  // truth (status/score/winner) without a full page reload.
  const refetchSessionMatches = async (sessionId: string) => {
    try {
      const entries = await getSessionMatches(sessionId);
      const ids = entries.flatMap((m) => [
        ...m.team1_player_ids,
        ...m.team2_player_ids,
      ]);
      const freshNames = await resolveNames(ids);
      setMatches((prev) => {
        const cur = prev[sessionId];
        return {
          ...prev,
          [sessionId]: {
            loading: false,
            entries,
            pool: cur?.pool,
            names: { ...(cur?.names ?? {}), ...freshNames },
          },
        };
      });
    } catch {
      // A refetch failure leaves the prior (now-stale) list in place rather than
      // blanking it — the mutation itself already succeeded; we never fabricate.
    }
  };

  const openCreate = () => {
    setDivName("");
    setDivMaxPlayers("16");
    setDivMinTier(TIER_ANY);
    setDivMaxTier(TIER_ANY);
    setCreateOpen(true);
  };

  // Open the matchup generator for a session. Resets the team rosters + stage so
  // a prior session's selection never leaks into a new dialog.
  const openAddMatch = (sessionId: string) => {
    setMatchSessionId(sessionId);
    setTeam1Ids([]);
    setTeam2Ids([]);
    setMatchStage("1");
    setMatchFormError(null);
    setMatchOpen(true);
  };

  // Toggle a pool player onto/off a team. Toggling a player onto one team does
  // NOT auto-remove them from the other — the double-assignment GUARD blocks the
  // create and surfaces a message, so the operator sees + fixes the conflict.
  const toggleTeamMember = (team: 1 | 2, playerId: string) => {
    setMatchFormError(null);
    const setter = team === 1 ? setTeam1Ids : setTeam2Ids;
    setter((prev) =>
      prev.includes(playerId)
        ? prev.filter((p) => p !== playerId)
        : [...prev, playerId],
    );
  };

  // Parse the stage input once for both the POST and a sane default. A blank or
  // non-numeric stage falls back to 1 (the backend default).
  const parsedStage = (() => {
    const n = Number.parseInt(matchStage, 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  })();

  const handleCreateMatch = async () => {
    if (!matchSessionId) return;
    // Client guards (minimal — the backend is authoritative on league-format team
    // sizes): each team needs >= 1 player, and no player may be on BOTH teams.
    if (team1Ids.length === 0 || team2Ids.length === 0) {
      setMatchFormError("Each team needs at least one player.");
      return;
    }
    const overlap = team1Ids.find((p) => team2Ids.includes(p));
    if (overlap) {
      setMatchFormError(
        "A player cannot be on both teams. Remove the duplicate before creating the match.",
      );
      return;
    }
    if (matchInFlight.current) return;
    matchInFlight.current = true;
    setSavingMatch(true);
    try {
      // court_id is OMITTED this slice — no court picker.
      await createMatch(matchSessionId, {
        stage: parsedStage,
        team1_player_ids: team1Ids,
        team2_player_ids: team2Ids,
      });
      // Refetch the session's matches so the new row reflects server truth (and
      // any names not yet resolved). Never optimistically inject a phantom row.
      await refetchSessionMatches(matchSessionId);
      notifications.success("Match created.");
      setMatchOpen(false);
    } catch (err) {
      // Failure (incl. RBAC 403): honest notifications, dialog stays OPEN, no phantom row.
      notifications.error(
        err instanceof Error ? err.message : "Could not create the match.",
      );
    } finally {
      matchInFlight.current = false;
      setSavingMatch(false);
    }
  };

  // Open the report-result dialog for a scheduled match.
  const openReport = (match: LeagueMatchResponse) => {
    setResultMatchId(match.match_id);
    setResultSessionId(match.session_id);
    setResultScore("");
    setResultWinner(null);
    setResultOpen(true);
  };

  // The report button is gated on the same rules the handler enforces: a
  // non-empty (trimmed) score and a chosen winner.
  const resultValid = resultScore.trim().length > 0 && resultWinner !== null;

  const handleReportResult = async () => {
    if (!resultMatchId || !resultSessionId) return;
    const trimmedScore = resultScore.trim();
    // Client gates — never round-trip an invalid POST.
    if (!trimmedScore) return;
    if (resultWinner !== 1 && resultWinner !== 2) return;
    if (resultInFlight.current) return;
    resultInFlight.current = true;
    setSavingResult(true);
    try {
      await reportMatchResult(resultMatchId, {
        score: trimmedScore,
        winner_team: resultWinner,
      });
      // Refetch the session's matches so the row reflects the completed status +
      // score + winner from the server (never a fabricated client flip).
      await refetchSessionMatches(resultSessionId);
      notifications.success("Result reported.");
      setResultOpen(false);
    } catch (err) {
      // Failure (incl. RBAC 403): honest notifications, dialog stays OPEN, match unchanged.
      notifications.error(
        err instanceof Error ? err.message : "Could not report the result.",
      );
    } finally {
      resultInFlight.current = false;
      setSavingResult(false);
    }
  };

  // Parse the max_players input once for both the validity gate and the POST.
  const parsedMaxPlayers = Number.parseInt(divMaxPlayers, 10);

  // The tier-order guard: when BOTH bounds are real tiers, min's index in
  // LEAGUE_TIERS must be <= max's index. An "Any" bound is unbounded on that
  // side and never violates the range.
  const tierRangeValid = (() => {
    if (divMinTier === TIER_ANY || divMaxTier === TIER_ANY) return true;
    const minIdx = LEAGUE_TIERS.indexOf(divMinTier as LeagueTier);
    const maxIdx = LEAGUE_TIERS.indexOf(divMaxTier as LeagueTier);
    if (minIdx < 0 || maxIdx < 0) return true;
    return minIdx <= maxIdx;
  })();

  // The create button is gated on the same rules the handler enforces (defence
  // in depth): a trimmed name, a max_players that is a finite integer >= 1, and a
  // non-inverted tier range.
  const createValid =
    divName.trim().length > 0 &&
    Number.isFinite(parsedMaxPlayers) &&
    parsedMaxPlayers >= 1 &&
    tierRangeValid;

  const handleCreate = async () => {
    const trimmedName = divName.trim();
    // Client-side gates — none of these ever round-trips an invalid POST.
    if (!trimmedName) return;
    if (!Number.isFinite(parsedMaxPlayers) || parsedMaxPlayers < 1) return;
    if (!tierRangeValid) return;
    if (divInFlight.current) return;
    divInFlight.current = true;
    setSavingDiv(true);
    try {
      // "Any" maps to null; the client omits a null bound on the wire.
      const created = await createDivision(seasonId, {
        name: trimmedName,
        max_players: parsedMaxPlayers,
        min_tier: divMinTier === TIER_ANY ? null : (divMinTier as LeagueTier),
        max_tier: divMaxTier === TIER_ANY ? null : (divMaxTier as LeagueTier),
      });
      setDivisions((prev) => [...prev, created]);
      // A successful create clears any prior divisions-load error banner — the
      // list is now demonstrably loadable.
      setDivisionsError(null);
      notifications.success("Division created.");
      setCreateOpen(false);
    } catch (err) {
      // Failure (incl. RBAC 403): honest notifications, dialog stays OPEN, no phantom row.
      notifications.error(
        err instanceof Error ? err.message : "Could not create the division.",
      );
    } finally {
      divInFlight.current = false;
      setSavingDiv(false);
    }
  };

  const openSession = () => {
    setSessNumber("");
    setSessDate("");
    setSessStart("");
    setSessEnd("");
    setSessionOpen(true);
  };

  // Parse the session_number input once for both the validity gate and the POST.
  const parsedSessNumber = Number.parseInt(sessNumber, 10);

  // The time-order guard: end_time must be strictly AFTER start_time. Bare
  // "HH:MM" strings compare lexicographically the same as chronologically within
  // a single day, so a plain string compare is correct. Equal or inverted is
  // invalid. An empty bound is not yet valid (the required gate handles that).
  const sessTimesValid =
    sessStart !== "" && sessEnd !== "" && sessEnd > sessStart;

  // The create button is gated on the same rules the handler enforces (defence
  // in depth): a session_number that is a finite integer >= 1, a scheduled date,
  // and a strictly-ordered start/end time.
  const sessionValid =
    Number.isFinite(parsedSessNumber) &&
    parsedSessNumber >= 1 &&
    sessDate.trim().length > 0 &&
    sessTimesValid;

  const handleCreateSession = async () => {
    // Client-side gates — none of these ever round-trips an invalid POST.
    if (!Number.isFinite(parsedSessNumber) || parsedSessNumber < 1) return;
    if (!sessDate.trim()) return;
    if (!sessStart || !sessEnd || sessEnd <= sessStart) return;
    if (sessInFlight.current) return;
    sessInFlight.current = true;
    setSavingSess(true);
    try {
      // The client widens scheduled_date/start_time/end_time to UTC ISO instants.
      const created = await createSession(seasonId, {
        session_number: parsedSessNumber,
        scheduled_date: sessDate,
        start_time: sessStart,
        end_time: sessEnd,
      });
      setSessions((prev) => [...prev, created]);
      // A successful create clears any prior sessions-load error banner — the
      // list is now demonstrably loadable.
      setSessionsError(null);
      notifications.success("Session created.");
      setSessionOpen(false);
    } catch (err) {
      // Failure (incl. RBAC 403): honest notifications, dialog stays OPEN, no phantom row.
      notifications.error(
        err instanceof Error ? err.message : "Could not create the session.",
      );
    } finally {
      sessInFlight.current = false;
      setSavingSess(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 2 }}>
      {loading ? (
        <Stack aria-label="Loading season" role="status" spacing={1}>
          <Skeleton height={36} variant="rounded" width={256} />
          <Skeleton height={24} variant="rounded" width={160} />
        </Stack>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : season ? (
        <>
          <Stack spacing={1.5}>
            <Link
              component={RouterLink}
              color="text.secondary"
              to={`/leagues/${season.league_id}`}
              underline="hover"
              variant="body2"
            >
              Back to league
            </Link>
            <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
              {season.name}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <Typography
                color="text.secondary"
                sx={{ fontVariantNumeric: "tabular-nums" }}
                variant="body2"
              >
                {formatDateRange(season.start_date, season.end_date)}
              </Typography>
              <Chip
                color={lifecycleColor(season.status)}
                label={SEASON_STATUS_LABELS[season.status] ?? season.status}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Stack>

          <Stack component="section" spacing={2}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography component="h2" variant="h6">
                Divisions
              </Typography>
              {sortedDivisions.length > 0 ? (
                <Button onClick={openCreate} size="small" variant="contained">
                  New division
                </Button>
              ) : null}
            </Stack>

            {divisionsError ? (
              <Alert severity="error">{divisionsError}</Alert>
            ) : sortedDivisions.length === 0 ? (
              <Paper
                sx={{
                  borderStyle: "dashed",
                  px: 2,
                  py: 6,
                  textAlign: "center",
                }}
                variant="outlined"
              >
                <Typography component="h3" variant="subtitle1">
                  No divisions yet
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Create the first division to group players by tier.
                </Typography>
                <Button
                  onClick={openCreate}
                  size="small"
                  sx={{ mt: 2 }}
                  variant="contained"
                >
                  New division
                </Button>
              </Paper>
            ) : (
              <Paper variant="outlined">
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {sortedDivisions.map((division) => {
                    const isOpen = !!expanded[division.division_id];
                    const reg = regs[division.division_id];
                    const standOpen = !!standingsOpen[division.division_id];
                    const stand = standings[division.division_id];
                    // Standings sorted by points DESC, then wins DESC as a
                    // tiebreak (documented choice; the server order is not trusted).
                    const sortedStandings = stand?.entries
                      ? [...stand.entries].sort(
                          (a, b) => b.points - a.points || b.wins - a.wins,
                        )
                      : undefined;
                    return (
                      <Box
                        component="li"
                        key={division.division_id}
                        sx={{
                          "&:not(:last-child)": {
                            borderBottom: 1,
                            borderColor: "divider",
                          },
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={2}
                          sx={{
                            alignItems: { md: "center" },
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ fontWeight: 600 }}
                              variant="body2"
                            >
                              {division.name}
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                              {tierRangeLabel(
                                division.min_tier,
                                division.max_tier,
                              )}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            sx={{
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Typography
                              color="text.secondary"
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                              variant="body2"
                            >
                              {division.registration_count} /{" "}
                              {division.max_players}
                            </Typography>
                            <Chip
                              color={lifecycleColor(division.status)}
                              label={
                                DIVISION_STATUS_LABELS[division.status] ??
                                division.status
                              }
                              size="small"
                              variant="outlined"
                            />
                            <Button
                              aria-expanded={isOpen}
                              onClick={() => toggleExpand(division)}
                              size="small"
                              variant="outlined"
                            >
                              {isOpen
                                ? "Hide registrations"
                                : "View registrations"}
                            </Button>
                            <Button
                              aria-expanded={standOpen}
                              onClick={() => toggleStandings(division)}
                              size="small"
                              variant="outlined"
                            >
                              {standOpen ? "Hide standings" : "Standings"}
                            </Button>
                          </Stack>
                        </Stack>

                        {isOpen ? (
                          <Box
                            sx={{
                              bgcolor: "action.hover",
                              borderTop: 1,
                              borderColor: "divider",
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            {reg?.loading ? (
                              <Stack
                                aria-label={`Loading registrations for ${division.name}`}
                                role="status"
                                spacing={1}
                              >
                                <Skeleton
                                  height={24}
                                  variant="rounded"
                                  width={160}
                                />
                                <Skeleton
                                  height={24}
                                  variant="rounded"
                                  width={128}
                                />
                              </Stack>
                            ) : reg?.error ? (
                              <Alert severity="error">{reg.error}</Alert>
                            ) : reg?.entries && reg.entries.length === 0 ? (
                              <Typography
                                color="text.secondary"
                                variant="body2"
                              >
                                No registrations yet.
                              </Typography>
                            ) : reg?.entries ? (
                              <Stack
                                component="ul"
                                divider={<Divider flexItem />}
                                sx={{ listStyle: "none", m: 0, p: 0 }}
                              >
                                {reg.entries.map((registration) => (
                                  <Stack
                                    component="li"
                                    direction="row"
                                    key={registration.registration_id}
                                    spacing={2}
                                    sx={{
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      py: 1,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {reg.names[registration.player_id] ??
                                        registration.player_id.slice(0, 8)}
                                    </Typography>
                                    <Chip
                                      color={lifecycleColor(
                                        registration.status,
                                      )}
                                      label={
                                        REGISTRATION_STATUS_LABELS[
                                          registration.status
                                        ] ?? registration.status
                                      }
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Stack>
                                ))}
                              </Stack>
                            ) : null}
                          </Box>
                        ) : null}

                        {standOpen ? (
                          <Box
                            sx={{
                              bgcolor: "action.hover",
                              borderTop: 1,
                              borderColor: "divider",
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            {stand?.loading ? (
                              <Stack
                                aria-label={`Loading standings for ${division.name}`}
                                role="status"
                                spacing={1}
                              >
                                <Skeleton
                                  height={24}
                                  variant="rounded"
                                  width={192}
                                />
                                <Skeleton
                                  height={24}
                                  variant="rounded"
                                  width={160}
                                />
                              </Stack>
                            ) : stand?.error ? (
                              <Alert severity="error">{stand.error}</Alert>
                            ) : sortedStandings &&
                              sortedStandings.length === 0 ? (
                              <Typography
                                color="text.secondary"
                                variant="body2"
                              >
                                No standings yet.
                              </Typography>
                            ) : sortedStandings ? (
                              <Box sx={{ overflowX: "auto" }}>
                                <Table
                                  aria-label={`${division.name} standings`}
                                  size="small"
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>#</TableCell>
                                      <TableCell>Player</TableCell>
                                      <TableCell align="right">W</TableCell>
                                      <TableCell align="right">L</TableCell>
                                      <TableCell align="right">Pts</TableCell>
                                      <TableCell align="right">MP</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {sortedStandings.map((standing, index) => (
                                      <TableRow key={standing.standing_id}>
                                        <TableCell
                                          sx={{
                                            fontVariantNumeric: "tabular-nums",
                                          }}
                                        >
                                          {index + 1}.
                                        </TableCell>
                                        <TableCell>
                                          {stand?.names[standing.player_id] ??
                                            standing.player_id.slice(0, 8)}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            fontVariantNumeric: "tabular-nums",
                                          }}
                                        >
                                          {standing.wins}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            fontVariantNumeric: "tabular-nums",
                                          }}
                                        >
                                          {standing.losses}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            fontVariantNumeric: "tabular-nums",
                                          }}
                                        >
                                          {standing.points}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            fontVariantNumeric: "tabular-nums",
                                          }}
                                        >
                                          {standing.matches_played}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            ) : null}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            )}
          </Stack>

          <Stack component="section" spacing={2}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography component="h2" variant="h6">
                Sessions
              </Typography>
              {sortedSessions.length > 0 ? (
                <Button onClick={openSession} size="small" variant="contained">
                  New session
                </Button>
              ) : null}
            </Stack>

            {sessionsError ? (
              <Alert severity="error">{sessionsError}</Alert>
            ) : sortedSessions.length === 0 ? (
              <Paper
                sx={{
                  borderStyle: "dashed",
                  px: 2,
                  py: 6,
                  textAlign: "center",
                }}
                variant="outlined"
              >
                <Typography component="h3" variant="subtitle1">
                  No sessions yet
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Schedule the first play session for this season.
                </Typography>
                <Button
                  onClick={openSession}
                  size="small"
                  sx={{ mt: 2 }}
                  variant="contained"
                >
                  New session
                </Button>
              </Paper>
            ) : (
              <Paper variant="outlined">
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {sortedSessions.map((session) => {
                    const matchesIsOpen = !!matchesOpen[session.session_id];
                    const match = matches[session.session_id];
                    // Matches ordered oldest-first by created_at (stable,
                    // deterministic) — never trust the server order. A row with an
                    // unparseable created_at sorts last.
                    const sortedMatches = match?.entries
                      ? [...match.entries].sort((a, b) => {
                          const at = new Date(a.created_at).getTime();
                          const bt = new Date(b.created_at).getTime();
                          const av = Number.isNaN(at) ? Infinity : at;
                          const bv = Number.isNaN(bt) ? Infinity : bt;
                          return av - bv;
                        })
                      : undefined;
                    // Resolve a team's roster to display names (never a raw UID).
                    const teamNames = (ids: string[]) =>
                      ids
                        .map((pid) => match?.names[pid] ?? pid.slice(0, 8))
                        .join(", ");
                    return (
                      <Box
                        component="li"
                        key={session.session_id}
                        sx={{
                          "&:not(:last-child)": {
                            borderBottom: 1,
                            borderColor: "divider",
                          },
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          sx={{
                            alignItems: { sm: "center" },
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ fontWeight: 600 }}
                              variant="body2"
                            >
                              Session {session.session_number}
                            </Typography>
                            <Typography
                              color="text.secondary"
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                              variant="body2"
                            >
                              {formatDate(session.scheduled_date)} ·{" "}
                              {formatTimeRange(
                                session.start_time,
                                session.end_time,
                              )}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            {!matchesIsOpen ? (
                              <Chip
                                color={lifecycleColor(session.status)}
                                label={
                                  SESSION_STATUS_LABELS[session.status] ??
                                  session.status
                                }
                                size="small"
                                variant="outlined"
                              />
                            ) : null}
                            <Button
                              aria-expanded={matchesIsOpen}
                              onClick={() => toggleMatches(session)}
                              size="small"
                              variant="outlined"
                            >
                              {matchesIsOpen ? "Hide matches" : "Matches"}
                            </Button>
                          </Stack>
                        </Stack>

                        {matchesIsOpen ? (
                          <Box
                            sx={{
                              bgcolor: "action.hover",
                              borderTop: 1,
                              borderColor: "divider",
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            {match?.loading ? (
                              <Stack
                                aria-label={`Loading matches for session ${session.session_number}`}
                                role="status"
                                spacing={1}
                              >
                                <Skeleton
                                  height={24}
                                  variant="rounded"
                                  width={224}
                                />
                                <Skeleton
                                  height={24}
                                  variant="rounded"
                                  width={160}
                                />
                              </Stack>
                            ) : match?.error ? (
                              <Alert severity="error">{match.error}</Alert>
                            ) : sortedMatches ? (
                              <Stack spacing={1.5}>
                                <Box sx={{ textAlign: "right" }}>
                                  <Button
                                    onClick={() =>
                                      openAddMatch(session.session_id)
                                    }
                                    size="small"
                                    variant="contained"
                                  >
                                    Add match
                                  </Button>
                                </Box>
                                {sortedMatches.length === 0 ? (
                                  <Typography
                                    color="text.secondary"
                                    variant="body2"
                                  >
                                    No matches yet.
                                  </Typography>
                                ) : (
                                  <Stack
                                    component="ul"
                                    divider={<Divider flexItem />}
                                    sx={{ listStyle: "none", m: 0, p: 0 }}
                                  >
                                    {sortedMatches.map((leagueMatch) => {
                                      const isCompleted =
                                        leagueMatch.status === "completed";
                                      return (
                                        <Stack
                                          component="li"
                                          direction={{
                                            xs: "column",
                                            md: "row",
                                          }}
                                          key={leagueMatch.match_id}
                                          spacing={2}
                                          sx={{
                                            alignItems: { md: "center" },
                                            justifyContent: "space-between",
                                            py: 1.25,
                                          }}
                                        >
                                          <Box>
                                            <Typography variant="body2">
                                              <Box
                                                component="span"
                                                sx={{ fontWeight: 600 }}
                                              >
                                                Team 1
                                              </Box>{" "}
                                              {teamNames(
                                                leagueMatch.team1_player_ids,
                                              )}{" "}
                                              <Box
                                                component="span"
                                                sx={{ color: "text.secondary" }}
                                              >
                                                vs
                                              </Box>{" "}
                                              <Box
                                                component="span"
                                                sx={{ fontWeight: 600 }}
                                              >
                                                Team 2
                                              </Box>{" "}
                                              {teamNames(
                                                leagueMatch.team2_player_ids,
                                              )}
                                            </Typography>
                                            <Typography
                                              color="text.secondary"
                                              sx={{
                                                fontVariantNumeric:
                                                  "tabular-nums",
                                              }}
                                              variant="caption"
                                            >
                                              Stage {leagueMatch.stage}
                                              {leagueMatch.score
                                                ? ` · ${leagueMatch.score}`
                                                : ""}
                                              {leagueMatch.winner_team === 1 ||
                                              leagueMatch.winner_team === 2
                                                ? ` · Team ${leagueMatch.winner_team} won`
                                                : ""}
                                            </Typography>
                                          </Box>
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ alignItems: "center" }}
                                          >
                                            <Chip
                                              color={lifecycleColor(
                                                leagueMatch.status,
                                              )}
                                              label={
                                                MATCH_STATUS_LABELS[
                                                  leagueMatch.status
                                                ] ?? leagueMatch.status
                                              }
                                              size="small"
                                              variant="outlined"
                                            />
                                            {!isCompleted ? (
                                              <Button
                                                onClick={() =>
                                                  openReport(leagueMatch)
                                                }
                                                size="small"
                                                variant="outlined"
                                              >
                                                Report result
                                              </Button>
                                            ) : null}
                                          </Stack>
                                        </Stack>
                                      );
                                    })}
                                  </Stack>
                                )}
                              </Stack>
                            ) : null}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            )}
          </Stack>
        </>
      ) : null}

      <Dialog
        aria-labelledby="new-division-title"
        onClose={() => {
          if (!savingDiv) setCreateOpen(false);
        }}
        open={createOpen}
      >
        <DialogTitle id="new-division-title">New division</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              disabled={savingDiv}
              id="division-name"
              label="Name"
              onChange={(event) => setDivName(event.target.value)}
              placeholder="e.g. Open Doubles"
              required
              size="small"
              slotProps={{ htmlInput: { maxLength: 255 } }}
              value={divName}
            />

            <TextField
              disabled={savingDiv}
              id="division-max-players"
              label="Max players"
              onChange={(event) => setDivMaxPlayers(event.target.value)}
              required
              size="small"
              slotProps={{ htmlInput: { min: 1 } }}
              type="number"
              value={divMaxPlayers}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl disabled={savingDiv} fullWidth size="small">
                <InputLabel id="division-min-tier-label">Min tier</InputLabel>
                <Select
                  id="division-min-tier"
                  label="Min tier"
                  labelId="division-min-tier-label"
                  onChange={(event) => setDivMinTier(event.target.value)}
                  value={divMinTier}
                >
                  <MenuItem value={TIER_ANY}>Any</MenuItem>
                  {LEAGUE_TIERS.map((tier) => (
                    <MenuItem key={tier} value={tier}>
                      {TIER_LABELS[tier]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl disabled={savingDiv} fullWidth size="small">
                <InputLabel id="division-max-tier-label">Max tier</InputLabel>
                <Select
                  id="division-max-tier"
                  label="Max tier"
                  labelId="division-max-tier-label"
                  onChange={(event) => setDivMaxTier(event.target.value)}
                  value={divMaxTier}
                >
                  <MenuItem value={TIER_ANY}>Any</MenuItem>
                  {LEAGUE_TIERS.map((tier) => (
                    <MenuItem key={tier} value={tier}>
                      {TIER_LABELS[tier]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {!tierRangeValid ? (
              <Alert severity="error">
                The minimum tier must not be higher than the maximum tier.
              </Alert>
            ) : null}
            {divMaxPlayers !== "" &&
            (!Number.isFinite(parsedMaxPlayers) || parsedMaxPlayers < 1) ? (
              <Alert severity="error">Max players must be at least 1.</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={savingDiv}
            onClick={() => setCreateOpen(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            disabled={savingDiv || !createValid}
            onClick={() => void handleCreate()}
            size="small"
            variant="contained"
          >
            {savingDiv ? "Creating…" : "Create division"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-labelledby="new-session-title"
        onClose={() => {
          if (!savingSess) setSessionOpen(false);
        }}
        open={sessionOpen}
      >
        <DialogTitle id="new-session-title">New session</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              disabled={savingSess}
              id="session-number"
              label="Session number"
              onChange={(event) => setSessNumber(event.target.value)}
              required
              size="small"
              slotProps={{ htmlInput: { min: 1 } }}
              type="number"
              value={sessNumber}
            />

            <TextField
              disabled={savingSess}
              id="session-date"
              label="Scheduled date"
              onChange={(event) => setSessDate(event.target.value)}
              required
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              type="date"
              value={sessDate}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                disabled={savingSess}
                fullWidth
                id="session-start"
                label="Start time"
                onChange={(event) => setSessStart(event.target.value)}
                required
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                type="time"
                value={sessStart}
              />
              <TextField
                disabled={savingSess}
                fullWidth
                id="session-end"
                label="End time"
                onChange={(event) => setSessEnd(event.target.value)}
                required
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                type="time"
                value={sessEnd}
              />
            </Stack>

            {sessNumber !== "" &&
            (!Number.isFinite(parsedSessNumber) || parsedSessNumber < 1) ? (
              <Alert severity="error">Session number must be at least 1.</Alert>
            ) : null}
            {sessStart !== "" && sessEnd !== "" && sessEnd <= sessStart ? (
              <Alert severity="error">
                The end time must be after the start time.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={savingSess}
            onClick={() => setSessionOpen(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            disabled={savingSess || !sessionValid}
            onClick={() => void handleCreateSession()}
            size="small"
            variant="contained"
          >
            {savingSess ? "Creating…" : "Create session"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-labelledby="new-match-title"
        fullWidth
        maxWidth="sm"
        onClose={() => {
          if (!savingMatch) setMatchOpen(false);
        }}
        open={matchOpen}
      >
        <DialogTitle id="new-match-title">New match</DialogTitle>
        <DialogContent>
          {(() => {
            // The matchmaking pool is the session's ACTIVELY-registered players
            // only. getSessionRegistrations returns ALL statuses (incl.
            // "cancelled" for a player who withdrew), and the manual create-match
            // path trusts the submitted ids — so this filter is the only gate
            // stopping a withdrawn player from being placed into a match (which
            // would persist + flow into ranked-match rating updates). Both the
            // team pickers AND the empty-state count read this filtered pool.
            const pool = (
              matchSessionId ? (matches[matchSessionId]?.pool ?? []) : []
            ).filter((p) => p.status === "registered");
            const nameOf = (pid: string) =>
              (matchSessionId && matches[matchSessionId]?.names[pid]) ||
              pid.slice(0, 8);
            return (
              <Stack spacing={2} sx={{ pt: 0.5 }}>
                <TextField
                  disabled={savingMatch}
                  id="match-stage"
                  label="Stage"
                  onChange={(event) => setMatchStage(event.target.value)}
                  size="small"
                  slotProps={{ htmlInput: { min: 1 } }}
                  type="number"
                  value={matchStage}
                />

                {pool.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    No registered players in this session yet.
                  </Typography>
                ) : (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <FormLabel>Team 1</FormLabel>
                      <Stack spacing={0.75}>
                        {pool.map((p) => {
                          const checked = team1Ids.includes(p.player_id);
                          const name = nameOf(p.player_id);
                          return (
                            <ToggleButton
                              aria-label={`Team 1 — ${name}`}
                              disabled={savingMatch}
                              fullWidth
                              key={`t1-${p.registration_id}`}
                              onClick={() => toggleTeamMember(1, p.player_id)}
                              selected={checked}
                              size="small"
                              sx={{
                                justifyContent: "flex-start",
                                textTransform: "none",
                              }}
                              value={p.player_id}
                            >
                              {name}
                            </ToggleButton>
                          );
                        })}
                      </Stack>
                    </Stack>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <FormLabel>Team 2</FormLabel>
                      <Stack spacing={0.75}>
                        {pool.map((p) => {
                          const checked = team2Ids.includes(p.player_id);
                          const name = nameOf(p.player_id);
                          return (
                            <ToggleButton
                              aria-label={`Team 2 — ${name}`}
                              disabled={savingMatch}
                              fullWidth
                              key={`t2-${p.registration_id}`}
                              onClick={() => toggleTeamMember(2, p.player_id)}
                              selected={checked}
                              size="small"
                              sx={{
                                justifyContent: "flex-start",
                                textTransform: "none",
                              }}
                              value={p.player_id}
                            >
                              {name}
                            </ToggleButton>
                          );
                        })}
                      </Stack>
                    </Stack>
                  </Stack>
                )}

                {matchFormError ? (
                  <Alert severity="error">{matchFormError}</Alert>
                ) : null}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={savingMatch}
            onClick={() => setMatchOpen(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            disabled={savingMatch}
            onClick={() => void handleCreateMatch()}
            size="small"
            variant="contained"
          >
            {savingMatch ? "Creating…" : "Add match"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-labelledby="report-result-title"
        onClose={() => {
          if (!savingResult) setResultOpen(false);
        }}
        open={resultOpen}
      >
        <DialogTitle id="report-result-title">Report result</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              disabled={savingResult}
              id="result-score"
              label="Score"
              onChange={(event) => setResultScore(event.target.value)}
              placeholder="e.g. 21-15, 21-18"
              required
              size="small"
              value={resultScore}
            />

            <FormControl disabled={savingResult}>
              <FormLabel id="result-winner-label">Winner</FormLabel>
              <RadioGroup
                aria-labelledby="result-winner-label"
                onChange={(event) =>
                  setResultWinner(event.target.value === "1" ? 1 : 2)
                }
                row
                value={resultWinner === null ? "" : String(resultWinner)}
              >
                <FormControlLabel
                  control={<Radio size="small" />}
                  label="Team 1"
                  value="1"
                />
                <FormControlLabel
                  control={<Radio size="small" />}
                  label="Team 2"
                  value="2"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={savingResult}
            onClick={() => setResultOpen(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            disabled={savingResult || !resultValid}
            onClick={() => void handleReportResult()}
            size="small"
            variant="contained"
          >
            {savingResult ? "Saving…" : "Report result"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
